import Seo, { articleSchema, breadcrumbSchema, faqSchema } from "@/components/Seo";
import Crumbs from "@/components/Crumbs";
import HelpCta from "@/components/HelpCta";
import { Clock, Activity, Brain, Heart, Users, AlertTriangle } from "lucide-react";

const TIMELINE = [
  { when: "Segundos 1–8", what: "Absorción pulmonar, peak cerebral, dopamina disparada" },
  { when: "Minutos 1–15", what: "Euforia, taquicardia, dilatación pupilar, omnipotencia" },
  { when: "Minutos 15–45", what: "Bajón abrupto, ansiedad, craving" },
  { when: "Días 1–7", what: "Insomnio, paranoia, pérdida de apetito extrema" },
  { when: "Semanas", what: "Deterioro dental, quemaduras, agresividad, aislamiento" },
  { when: "Meses", what: "Desnutrición, daño pulmonar, alucinaciones persistentes" },
  { when: "Años", what: "Daño neurológico permanente, deterioro cognitivo, situación de calle" },
];

const INMEDIATOS = [
  "Euforia intensa pero breve (8–15 minutos)",
  "Taquicardia y sudoración profusa",
  "Dilatación pupilar",
  "Sensación de poder e invulnerabilidad",
  "Bajón abrupto con angustia y necesidad de volver a consumir",
];

const CORTO = [
  "Pérdida de apetito extrema y rápida baja de peso",
  "Insomnio prolongado (2–3 días sin dormir)",
  "Paranoia y alucinaciones auditivas",
  "Agresividad e irritabilidad marcada",
  'Deterioro dental rápido ("boca de pasta base")',
  "Quemaduras en labios y dedos por la pipa caliente",
];

const LARGO = [
  "Daño neurológico permanente (memoria, atención, funciones ejecutivas)",
  "Deterioro cognitivo progresivo",
  "Daño pulmonar severo por solventes inhalados",
  "Desnutrición crónica",
  "Deterioro total de relaciones sociales y laborales",
  "Situación de calle",
  "Enfermedades infecciosas (TBC, VIH) por compartir pipas",
];

const FAMILIA = [
  "Impacto emocional profundo en hijos y pareja",
  "Episodios de violencia intrafamiliar",
  "Pérdida económica significativa (empeños, robos, deudas)",
  "Codependencia y agotamiento del cuidador",
  "Ruptura de vínculos y aislamiento de la red extendida",
];

// FAQ interna de la página (para schema)
const innerFaq = [
  {
    question: "¿Los efectos de la pasta base son reversibles?",
    answer:
      "Muchos efectos son reversibles con tratamiento profesional prolongado: nutrición, estado dental con atención odontológica, funciones cognitivas parciales. Algunos daños son más difíciles de revertir, como el daño pulmonar severo y el deterioro cognitivo profundo. La intervención temprana es clave.",
  },
  {
    question: "¿Cuánto tiempo tarda en aparecer el deterioro dental?",
    answer:
      "El deterioro dental característico comienza a ser notorio entre los 2 y 4 meses de consumo sostenido, y puede avanzar rápidamente por la combinación de falta de higiene, desnutrición y exposición a solventes.",
  },
  {
    question: "¿La pasta base produce alucinaciones?",
    answer:
      "Sí. El consumo prolongado provoca episodios paranoicos y alucinaciones, principalmente auditivas y visuales. En algunos casos se instala una psicosis persistente que requiere tratamiento psiquiátrico especializado.",
  },
];

export default function Efectos() {
  const crumbs = [{ name: "Efectos", path: "/efectos" }];
  const schemas = [
    articleSchema({
      title: "Efectos de la Pasta Base: Inmediatos, a Corto y Largo Plazo",
      description:
        "Conoce los efectos inmediatos, a corto y largo plazo de la pasta base: desde la euforia inicial hasta el deterioro neurológico, dental y pulmonar irreversible.",
      path: "/efectos",
    }),
    breadcrumbSchema([{ name: "Inicio", path: "/" }, ...crumbs]),
    faqSchema(innerFaq),
  ];

  return (
    <>
      <Seo
        title="Efectos de la Pasta Base: Inmediatos, a Corto y Largo Plazo | pasta-base.cl"
        description="Conoce los efectos inmediatos, a corto y largo plazo de la pasta base: desde la euforia inicial hasta el deterioro neurológico, dental y pulmonar irreversible."
        path="/efectos"
        type="article"
        schema={schemas}
      />
      <article className="mx-auto max-w-5xl px-5 py-16 lg:px-8">
        <Crumbs items={crumbs} />
        <header className="max-w-3xl">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400">
            Análisis clínico
          </span>
          <h1 className="mt-3 text-4xl font-extrabold tracking-tight text-slate-50 sm:text-5xl">
            Efectos de la Pasta Base en el Cuerpo y la Mente
          </h1>
          <p className="mt-4 text-lg leading-relaxed text-slate-300">
            Un recorrido en orden cronológico: qué ocurre en los primeros
            segundos, qué pasa en las semanas siguientes y qué secuelas deja
            años después. Con datos clínicos y observaciones de equipos de
            tratamiento chilenos.
          </p>
        </header>

        {/* Timeline visual */}
        <section className="mt-12 rounded-3xl border border-slate-800 bg-slate-900/50 p-6 sm:p-8">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-emerald-300" />
            <h2 className="text-xl font-bold text-slate-50">
              Progresión temporal de efectos
            </h2>
          </div>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {TIMELINE.map((t) => (
              <div
                key={t.when}
                className="rounded-xl border border-slate-800 bg-slate-950/60 p-4"
              >
                <div className="text-xs font-bold uppercase tracking-[0.15em] text-emerald-400">
                  {t.when}
                </div>
                <div className="mt-2 text-sm text-slate-200">{t.what}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Efectos inmediatos */}
        <SectionBlock
          icon={Activity}
          color="text-red-400"
          ring="ring-red-500/20"
          bg="bg-red-500/10"
          subtitle="Primeros 5 a 20 segundos tras fumar"
          title="Efectos inmediatos"
        >
          <p>
            Fumar pasta base desencadena una serie de efectos físicos y
            psicológicos extremadamente rápidos. La persona siente primero una
            euforia intensa —la llamada "taquicardia del alma"— acompañada de
            un torrente de sensación de poder y omnipotencia. Esa fase,
            breve y engañosa, se paga muy caro segundos después: aparece un
            bajón profundo que solo se alivia volviendo a fumar.
          </p>
          <ul>
            {INMEDIATOS.map((i) => (
              <li key={i}>{i}</li>
            ))}
          </ul>
        </SectionBlock>

        {/* Corto plazo */}
        <SectionBlock
          icon={AlertTriangle}
          color="text-amber-300"
          ring="ring-amber-500/20"
          bg="bg-amber-500/10"
          subtitle="Días a semanas de consumo regular"
          title="Efectos a corto plazo"
        >
          <p>
            Cuando el consumo se vuelve regular —aunque sea solo por algunas
            semanas— aparecen signos que transforman el cuerpo y la conducta de
            la persona. El deterioro empieza a ser visible: baja de peso,
            quemaduras, irritabilidad. La salud mental también se afecta con
            cuadros de paranoia, ansiedad severa y alucinaciones auditivas.
          </p>
          <ul>
            {CORTO.map((i) => (
              <li key={i}>{i}</li>
            ))}
          </ul>
        </SectionBlock>

        {/* Largo plazo */}
        <SectionBlock
          icon={Brain}
          color="text-cyan-300"
          ring="ring-cyan-500/20"
          bg="bg-cyan-500/10"
          subtitle="Meses a años de consumo sostenido"
          title="Efectos a largo plazo"
        >
          <p>
            El consumo prolongado de pasta base deja huellas profundas, muchas
            de ellas difíciles de revertir. El sistema nervioso, los pulmones,
            el estado general de salud y los vínculos sociales sufren un
            deterioro acumulativo. La esperanza de vida de un consumidor
            crónico sin tratamiento se reduce significativamente respecto a la
            población general.
          </p>
          <ul>
            {LARGO.map((i) => (
              <li key={i}>{i}</li>
            ))}
          </ul>
          <p>
            La buena noticia: la intervención profesional oportuna puede
            detener el avance del deterioro y recuperar buena parte del
            funcionamiento perdido, aunque requiere tratamiento largo y apoyo
            familiar sostenido.
          </p>
        </SectionBlock>

        {/* Efectos en familia */}
        <SectionBlock
          icon={Users}
          color="text-emerald-300"
          ring="ring-emerald-500/20"
          bg="bg-emerald-500/10"
          subtitle="Impacto en el sistema familiar"
          title="Efectos en la familia"
        >
          <p>
            La adicción nunca afecta a una sola persona: sacude a toda la red
            de personas que la rodean. La familia del consumidor vive su
            propio proceso de dolor, culpa, miedo y, con frecuencia, agotamiento
            extremo. Por eso los tratamientos de calidad incluyen al grupo
            familiar como parte activa.
          </p>
          <ul>
            {FAMILIA.map((i) => (
              <li key={i}>{i}</li>
            ))}
          </ul>
        </SectionBlock>

        {/* CTA */}
        <div className="mt-14 flex flex-wrap items-center gap-4 rounded-3xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/10 via-slate-900 to-slate-950 p-6 sm:p-8">
          <Heart className="h-6 w-6 shrink-0 text-emerald-300" />
          <p className="flex-1 text-base text-slate-200 sm:text-lg">
            <strong className="font-semibold text-emerald-300">Los efectos son reversibles con tratamiento.</strong>{" "}
            Busca ayuda hoy: hay profesionales preparados y gratuitos esperando
            tu llamada.
          </p>
          <a
            href="https://sinadicciones.org/centros-de-rehabilitacion-drogas-chile"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="efectos-help-btn"
            className="btn-press inline-flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-3 text-sm font-semibold text-slate-950 hover:bg-emerald-400"
          >
            Buscar Centros de Rehabilitación
          </a>
        </div>
      </article>

      <section className="border-t border-slate-800 bg-slate-950">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
          <HelpCta testidPrefix="efectos" />
        </div>
      </section>
    </>
  );
}

function SectionBlock({ icon: Icon, color, ring, bg, title, subtitle, children }) {
  return (
    <section className="mt-14">
      <div className="flex items-start gap-4">
        <div className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ring-1 ${bg} ${ring}`}>
          <Icon className={`h-5 w-5 ${color}`} />
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
            {subtitle}
          </div>
          <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
            {title}
          </h2>
        </div>
      </div>
      <div className="prose-dark mt-6 pl-0 sm:pl-[60px]">{children}</div>
    </section>
  );
}
